import { Offcanvas, Row } from 'react-bootstrap';
import React from 'react';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import { FormProvider, useForm } from 'react-hook-form';
import { AddEntityDrawerProps } from '../IAP/EntityListProps';
import { useCreateIncidentAction } from './hooks/incidentActionHooks';
import { useCreateAction } from './hooks/actionHooks';
import DrawerWrapper, { DrawerFooter } from '../IAP/DrawerWrapper';
import {
  selectIncidentGuid,
  useAppSelector,
} from '../../slices/commonSelectors';
import { ACTION_STATUS_OPTIONS, INCIDENT } from 'components/IAP/ICSConstants';
import FormTextField from '../IAP/formFields/FormTextField';
import FormSelectField from '../IAP/formFields/FormSelectField';
import FormCheckboxField from '../IAP/formFields/FormCheckboxField';
import FormDateTimeField from '../IAP/formFields/FormDateTimeField';
import { ActionBase } from '../../types/iapBaseTypes';
import { useIncidentRoster } from '../IAP/hooks/incidentRoster/useIncidentRoster';

type FormFields = ActionBase & {
  poc: string;
  briefed_poc: boolean;
  start_date: Date;
  status: string;
  target_date: Date;
  actual_date: Date;
  resources: string[];
};

const OpenActionAddDrawer: React.FC<AddEntityDrawerProps> = ({
  toggle,
  scope = INCIDENT,
}) => {
  const form = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: {
      status: 'Planned',
      resources: [],
    },
  });
  const { handleSubmit, watch } = form;

  const watchedName = watch('name');

  const incidentId = useAppSelector(selectIncidentGuid);
  const { data: incidentRoster } = useIncidentRoster();
  const incidentActionMutation = useCreateIncidentAction();
  const organizationActionMutation = useCreateAction();

  const onNewActionDataSubmit = async (formData: FormFields) => {
    const basePayload = {
      ...formData,
      archived: false,
    };

    if (scope === INCIDENT) {
      const incidentPayload = {
        ...basePayload,
        incident_id: incidentId,
      };
      await incidentActionMutation.mutateAsync(incidentPayload);
    } else {
      await organizationActionMutation.mutateAsync(basePayload);
    }
    toggle();
  };

  return (
    <DrawerWrapper toggle={toggle} title="Add Action">
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onNewActionDataSubmit)}
          className="d-flex flex-column justify-content-between flex-grow-1"
        >
          <Offcanvas.Body>
            <Row>
              <FormTextField
                title="Name"
                fieldName="name"
                rules={{
                  required: { value: true, message: 'This field is required' },
                  maxLength: {
                    value: 250,
                    message: 'This maximum length is 250 characters',
                  },
                }}
                variant="half"
              />
            </Row>
            <Row>
              <FormSelectField
                title="Point of Contact"
                fieldName="poc"
                options={incidentRoster?.map((u) => {
                  return {
                    label: u.userName ?? (u.emailAddress as string),
                    value: u.userId,
                  };
                })}
                variant="half"
              />
              <FormCheckboxField
                title="Briefed POC"
                fieldName="briefed_poc"
                variant="half"
              />
            </Row>
            <Row>
              <FormSelectField
                title="Status"
                fieldName="status"
                options={ACTION_STATUS_OPTIONS}
                variant="full"
                rules={{
                  required: { value: true, message: 'Status is required' },
                }}
              />
            </Row>
            <Row>
              <FormDateTimeField
                title="Start Date & Time"
                fieldName="start_date"
                variant="full"
              />
            </Row>
            <Row>
              <FormDateTimeField
                title="Deadline Date & Time"
                fieldName="target_date"
                variant="full"
              />
            </Row>
          </Offcanvas.Body>
          <DrawerFooter>
            <div className="button-group">
              <StylishNewButton
                className="button--secondary button--reverse"
                type="button"
                onClick={toggle}
              >
                Close
              </StylishNewButton>
              <StylishNewButton
                className="button--primary"
                type="submit"
                disabled={
                  !watchedName ||
                  (scope === INCIDENT
                    ? incidentActionMutation.isPending
                    : organizationActionMutation.isPending)
                }
              >
                Save
              </StylishNewButton>
            </div>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerWrapper>
  );
};

export default OpenActionAddDrawer;
